import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Input, Label, LoadingState, Select } from '@buzzbee/ui';
import { useState } from 'react';
import { del, errorMessage, get, post, put } from '../lib/api';
import { useRouter } from '../lib/router';

type Account = { id: string; provider: 'google' | 'microsoft' | 'ics'; label: string; hint: string | null; status: 'connected' | 'error'; lastError: string | null; writable: boolean };
type Assignment = { id: string; locationId: string | null; staffId: string | null; accountId: string; calendarId: string; calendarName: string | null };
type CalendarState = {
  accounts: Account[];
  assignments: Assignment[];
  locations: { id: string; name: string; isPrimary: boolean }[];
  staff: { id: string; name: string; locationId: string | null }[];
  mode: 'instant' | 'request';
  available: { google: boolean; microsoft: boolean };
};
type CalendarOption = { id: string; name: string; primary: boolean };

const PROVIDER: Record<Account['provider'], string> = { google: 'Google', microsoft: 'Outlook / Microsoft 365', ics: 'Calendar link' };

/**
 * Settings, Calendars (also onboarding step 2): connected calendar accounts, which calendar each
 * location and staff member uses, and whether the receptionist books on the call or sends each
 * booking to the owner to confirm.
 */
export function CalendarSettings({ canEdit, returnTo = 'settings' }: { canEdit: boolean; returnTo?: 'settings' | 'onboarding' }) {
  const qc = useQueryClient();
  const { search } = useRouter();
  const q = useQuery({ queryKey: ['calendar'], queryFn: () => get<CalendarState>('/api/settings/calendar') });
  const [feedUrl, setFeedUrl] = useState('');
  const [feedLabel, setFeedLabel] = useState('');
  const [showFeed, setShowFeed] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['calendar'] });

  const connect = useMutation({
    mutationFn: (provider: 'google' | 'microsoft') => post<{ url: string }>(`/api/settings/calendar/oauth/${provider}/start`, { return: returnTo }),
    onSuccess: (r) => window.location.assign(r.url),
  });
  const saveFeed = useMutation({
    mutationFn: () => post<CalendarState>('/api/settings/calendar/feed', { url: feedUrl, label: feedLabel || undefined }),
    onSuccess: async () => {
      setFeedUrl('');
      setFeedLabel('');
      setShowFeed(false);
      setMessage({ ok: true, text: 'Calendar link added. Choose below which location or staff member it belongs to.' });
      await refresh();
    },
  });
  const test = useMutation({ mutationFn: (id: string) => post<{ ok: boolean; message: string }>(`/api/settings/calendar/accounts/${id}/test`), onSuccess: (r) => setMessage({ ok: r.ok, text: r.message }) });
  const remove = useMutation({
    mutationFn: (id: string) => del(`/api/settings/calendar/accounts/${id}`),
    onSuccess: async () => {
      setMessage({ ok: true, text: 'Removed. Locations and staff that used it are back on the BuzzBee calendar.' });
      await refresh();
    },
  });
  const setMode = useMutation({ mutationFn: (mode: 'instant' | 'request') => put('/api/settings/calendar/mode', { mode }), onSuccess: refresh });

  if (!q.data) return <LoadingState />;
  const { accounts, mode, available } = q.data;
  const returned = search.get('calendar');
  const error = connect.error ?? saveFeed.error ?? remove.error ?? setMode.error ?? test.error;

  return (
    <div className="space-y-6">
      {returned === 'connected' ? <Alert variant="success">Calendar account connected. Choose below which calendar each location and staff member uses.</Alert> : null}
      {returned === 'error' ? <Alert variant="destructive">{search.get('reason') ?? 'The calendar could not be connected.'}</Alert> : null}

      <section className="space-y-3">
        <h3 className="text-sm font-medium">Connected calendar accounts</h3>
        {accounts.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            None yet: bookings live in BuzzBee (see Bookings), and times booked anywhere else are invisible to your receptionist. If you also take bookings by phone, walk-in or on your website, connect
            that calendar or turn on &quot;confirm each booking&quot; below.
          </p>
        ) : (
          <ul className="space-y-2">
            {accounts.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center gap-2 rounded-xl p-3 ring-1 ring-border">
                <strong className="text-sm">{PROVIDER[a.provider]}</strong>
                <span className="text-sm text-muted-foreground">{a.label}</span>
                {a.hint ? <span className="figures text-xs text-muted-foreground">{a.hint}</span> : null}
                <Badge variant={a.status === 'error' ? 'destructive' : 'success'}>{a.status === 'error' ? 'Needs reconnecting' : a.writable ? 'Reads and adds bookings' : 'Reads busy times'}</Badge>
                {canEdit ? (
                  <span className="ml-auto flex gap-1">
                    <Button type="button" size="sm" variant="ghost" onClick={() => test.mutate(a.id)} loading={test.isPending && test.variables === a.id}>
                      Test
                    </Button>
                    {a.status === 'error' && a.provider !== 'ics' ? (
                      <Button type="button" size="sm" onClick={() => connect.mutate(a.provider as 'google' | 'microsoft')}>
                        Reconnect
                      </Button>
                    ) : null}
                    <Button type="button" size="sm" variant="ghost" onClick={() => remove.mutate(a.id)} loading={remove.isPending && remove.variables === a.id}>
                      Remove
                    </Button>
                  </span>
                ) : null}
                {a.status === 'error' && a.lastError ? <p className="w-full text-sm text-destructive">{a.lastError} Your receptionist takes booking requests for the calendars on this account until it is fixed.</p> : null}
              </li>
            ))}
          </ul>
        )}
        {canEdit ? (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" disabled={!available.google} onClick={() => connect.mutate('google')} loading={connect.isPending && connect.variables === 'google'}>
                Add Google account
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={!available.microsoft} onClick={() => connect.mutate('microsoft')} loading={connect.isPending && connect.variables === 'microsoft'}>
                Add Outlook / Microsoft 365
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setShowFeed(!showFeed)}>
                Add calendar link (iCal)
              </Button>
            </div>
            {!available.google || !available.microsoft ? (
              <p className="text-xs text-muted-foreground">
                {[!available.google ? 'Google' : null, !available.microsoft ? 'Outlook' : null].filter(Boolean).join(' and ')} sign-in is not switched on for this account yet; a calendar link works meanwhile.
              </p>
            ) : null}
            {showFeed ? (
              <div className="grid gap-2 rounded-xl p-4 ring-1 ring-border sm:grid-cols-[2fr_1fr]">
                <div className="space-y-1.5">
                  <Label htmlFor="cal-feed">Calendar link (iCal / ICS)</Label>
                  <Input id="cal-feed" placeholder="https://… or webcal://…" value={feedUrl} onChange={(e) => setFeedUrl(e.target.value)} autoComplete="off" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cal-feed-name">Name (optional)</Label>
                  <Input id="cal-feed-name" placeholder="e.g. Ava's calendar" value={feedLabel} onChange={(e) => setFeedLabel(e.target.value)} />
                </div>
                <p className="text-xs text-muted-foreground sm:col-span-2">
                  Apple Calendar: share it as a public calendar and copy the link. Google: Settings, the calendar, &quot;Secret address in iCal format&quot;. Booking tools and WordPress plugins often have an
                  &quot;iCal feed&quot; link. Read-only: busy times are respected, bookings stay in BuzzBee.
                </p>
                <div>
                  <Button type="button" size="sm" onClick={() => saveFeed.mutate()} loading={saveFeed.isPending} disabled={feedUrl.trim().length < 8}>
                    Check and add
                  </Button>
                </div>
              </div>
            ) : null}
            <p className="text-xs text-muted-foreground">
              A whole team on one account: in Google Calendar each person shares their calendar with your business account (&quot;Make changes to events&quot;), then connect only the business account and pick
              each person&apos;s calendar below. Or add each person&apos;s own account. WordPress booking plugins (Amelia, Bookly, BookingPress, LatePoint, Simply Schedule Appointments): turn on the
              plugin&apos;s Google or Outlook sync and use that same calendar here.
            </p>
          </div>
        ) : null}
      </section>

      {accounts.length ? <Assignments state={q.data} canEdit={canEdit} onError={(text) => setMessage({ ok: false, text })} /> : null}

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">When a caller picks a time</legend>
        {[
          { id: 'instant' as const, name: 'Book it on the call', desc: 'The time is booked straight away when it is free in your calendars.' },
          { id: 'request' as const, name: 'Send me each booking to confirm', desc: 'The caller is told the team will confirm. You get an email (and text, if set) with Confirm and Decline buttons. Reschedules keep the current time until you approve the new one.' },
        ].map((o) => (
          <label key={o.id} className={`flex items-start gap-3 rounded-xl p-4 ring-1 ${mode === o.id ? 'ring-primary' : 'ring-border'} ${canEdit ? 'cursor-pointer' : ''}`}>
            <input type="radio" name="booking-mode" className="mt-1" checked={mode === o.id} disabled={!canEdit || setMode.isPending} onChange={() => setMode.mutate(o.id)} />
            <span>
              <span className="block font-medium">{o.name}</span>
              <span className="block text-sm text-muted-foreground">{o.desc}</span>
            </span>
          </label>
        ))}
        <p className="text-xs text-muted-foreground">If a connected calendar cannot be reached during a call, that booking is always sent to you to confirm.</p>
      </fieldset>

      {error ? <Alert variant="destructive">{errorMessage(error)}</Alert> : message ? <Alert variant={message.ok ? 'success' : 'destructive'}>{message.text}</Alert> : null}
    </div>
  );
}

/** One row per location, with its staff under it: pick each one's calendar. */
function Assignments({ state, canEdit, onError }: { state: CalendarState; canEdit: boolean; onError: (text: string) => void }) {
  const qc = useQueryClient();
  const usable = state.accounts.filter((a) => a.status !== 'error');
  const lists = useQueries({
    queries: usable.map((a) => ({ queryKey: ['calendar', 'calendars', a.id], queryFn: () => get<{ calendars: CalendarOption[] }>(`/api/settings/calendar/accounts/${a.id}/calendars`) })),
  });
  const assign = useMutation({
    mutationFn: (v: { locationId?: string; staffId?: string; value: string }) => {
      const [accountId, ...rest] = v.value.split('|');
      return put('/api/settings/calendar/assignment', { locationId: v.locationId, staffId: v.staffId, accountId: accountId || null, calendarId: rest.join('|') || undefined });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calendar'] }),
    onError: (e) => onError(errorMessage(e)),
  });

  const options = [
    { value: '', label: 'BuzzBee calendar only' },
    ...usable.flatMap((a, i) =>
      (lists[i]?.data?.calendars ?? []).map((c) => ({ value: `${a.id}|${c.primary && a.provider !== 'ics' ? 'primary' : c.id}`, label: `${c.name}${c.primary && a.provider !== 'ics' ? ' (main)' : ''} · ${a.label}` })),
    ),
  ];
  const valueOf = (a: Assignment | undefined) => {
    if (!a) return '';
    const exact = `${a.accountId}|${a.calendarId}`;
    // Keep showing the saved choice even if the list has not loaded or no longer has it.
    if (!options.some((o) => o.value === exact)) options.push({ value: exact, label: `${a.calendarName ?? a.calendarId} · ${state.accounts.find((x) => x.id === a.accountId)?.label ?? ''}` });
    return exact;
  };
  const loading = lists.some((l) => l.isLoading);
  const failed = lists.find((l) => l.error);

  const row = (key: string, label: string, assignment: Assignment | undefined, target: { locationId?: string; staffId?: string }, hint: string) => (
    <div key={key} className="grid items-center gap-2 sm:grid-cols-[1fr_2fr]">
      <div>
        <span className="text-sm font-medium">{label}</span>
        <span className="block text-xs text-muted-foreground">{hint}</span>
      </div>
      <Select aria-label={`Calendar for ${label}`} value={valueOf(assignment)} disabled={!canEdit || assign.isPending} onChange={(e) => assign.mutate({ ...target, value: e.target.value })} options={options} />
    </div>
  );

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-sm font-medium">Which calendar each location and staff member uses</h3>
        <p className="text-xs text-muted-foreground">
          A location&apos;s calendar blocks everyone there (holidays, room bookings, website bookings). A staff calendar blocks only that person, and their bookings are added to it; without one, bookings
          go to the location&apos;s calendar.
        </p>
      </div>
      {loading ? <p className="text-sm text-muted-foreground">Loading your calendars…</p> : null}
      {failed ? <Alert variant="destructive">{errorMessage(failed.error)}</Alert> : null}
      {state.locations.map((loc) => {
        const people = state.staff.filter((s) => s.locationId === loc.id || (!s.locationId && loc.isPrimary));
        return (
          <div key={loc.id} className="space-y-3 rounded-xl p-4 ring-1 ring-border">
            {row(`loc-${loc.id}`, loc.name, state.assignments.find((a) => a.locationId === loc.id && !a.staffId), { locationId: loc.id }, 'Location calendar')}
            {people.length ? (
              <div className="space-y-2 border-l border-border pl-4">
                {people.map((p) => row(`staff-${p.id}`, p.name, state.assignments.find((a) => a.staffId === p.id), { staffId: p.id }, p.locationId ? 'Staff calendar' : 'Staff calendar (works at every location)'))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No staff at this location yet (add them under Hours and staff).</p>
            )}
          </div>
        );
      })}
    </section>
  );
}
